import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { isFindShortcut, isInActiveWindow } from './findShortcut'
import type { PreviewSearch } from './usePreviewSearch'

interface PreviewSearchBarProps {
  search: PreviewSearch
  className?: string
}

/**
 * The find bar of a document preview (#627, #755): the field, the hit counter
 * and the two stepping buttons. Ctrl+F / ⌘F focuses the field instead of
 * opening the browser's own find, which cannot see into a rendered PDF — but
 * only while the preview's window is the active one, so a chat and a Verifier
 * open side by side never both grab the chord.
 */
export function PreviewSearchBar({ search, className }: PreviewSearchBarProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { input, setInput, term, handleKey, total, position, next, previous } = search

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!isFindShortcut(e)) return
      const el = inputRef.current
      if (!el || !isInActiveWindow(el)) return
      e.preventDefault()
      el.focus()
      el.select()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <div className="relative min-w-0 flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-secondary)]" />
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder={t('filePreview.search.placeholder')}
          aria-label={t('filePreview.search.placeholder')}
          className="h-8 pl-8 text-xs"
        />
      </div>
      {term && (
        <div className="flex flex-shrink-0 items-center gap-1">
          <span
            className="whitespace-nowrap text-[11px] tabular-nums text-[var(--text-secondary)]"
            aria-live="polite"
          >
            {total === 0
              ? t('filePreview.search.noHits')
              : t('filePreview.search.position', { position, total })}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={total === 0}
            onClick={previous}
            title={t('filePreview.search.previous')}
            aria-label={t('filePreview.search.previous')}
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={total === 0}
            onClick={next}
            title={t('filePreview.search.next')}
            aria-label={t('filePreview.search.next')}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}
